import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";

import {
  PersonalizeToneOption,
  TONE_OPTIONS,
} from "features/Influencer/QuickSendIndividualDrawer/Dialogs/PersonalizeDialog/schema";

import ToneChips from "./ToneChips";
import { AI_INCLUDE_OPTIONS } from "./constants";
import styles from "./styles";

interface Props {
  open: boolean;
  brandName: string;
  creditsLeft: number;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (tone: PersonalizeToneOption | null, notes: string) => void;
}

export default function RegenerateDialog({
  open,
  brandName,
  creditsLeft,
  loading = false,
  onClose,
  onSubmit,
}: Props) {
  const [checked, setChecked] = useState<string[]>([]);
  const [tone, setTone] = useState<PersonalizeToneOption | null>(null);
  const [notes, setNotes] = useState("");

  const toggle = (value: string) =>
    setChecked((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );

  const handleSubmit = () => {
    const parts = [...checked];
    if (notes.trim()) parts.push(notes.trim());
    onSubmit(tone, parts.join(". "));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={styles.rmTitle}>
          <Box
            component="i"
            className="fa-solid fa-wand-magic-sparkles"
            sx={styles.rmTitleIcon}
          />
          Personalize This Draft
        </Typography>
        <Typography sx={styles.rmSub}>
          Select prompts to customize your pitch for {brandName}.{" "}
          <Box component="strong" sx={{ fontWeight: 700 }}>
            Uses 1 credit.
          </Box>
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography sx={styles.rmGroupLabel}>
          What should AI include?
        </Typography>
        {AI_INCLUDE_OPTIONS.map((prompt) => {
          const on = checked.includes(prompt);
          return (
            <Box
              key={prompt}
              onClick={() => toggle(prompt)}
              sx={styles.rmCheck(on)}
            >
              <Box sx={styles.rmCheckBox(on)}>
                {on && <Box component="i" className="fa-solid fa-check" />}
              </Box>
              {prompt}
            </Box>
          );
        })}

        <Typography sx={styles.rmGroupLabel}>Adjust tone or length?</Typography>
        <ToneChips
          options={TONE_OPTIONS}
          value={tone}
          onChange={setTone}
          grid
        />

        <Typography sx={styles.rmGroupLabel}>Anything else?</Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Focus on video content, mention TikTok experience…"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Typography sx={styles.rmCredits}>
          <Box component="i" className="fa-solid fa-coins" sx={{ mr: 0.5 }} />
          {creditsLeft} credits left
        </Typography>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={loading}
          onClick={handleSubmit}
          sx={{ textTransform: "none" }}
        >
          <Box
            component="i"
            className="fa-solid fa-sparkles"
            sx={{ mr: 0.5 }}
          />
          Rewrite Draft
        </Button>
      </DialogActions>
    </Dialog>
  );
}
